'use client';

import { useState, useEffect, useRef } from 'react';
import Link from 'next/link';
import { loadSearchIndex, search, SearchResult } from '@/lib/search';
import { trackSearch } from '@/lib/analytics';

export default function Search() {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<SearchResult[]>([]);
  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const searchRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Load search index on mount
  useEffect(() => {
    loadSearchIndex().catch(console.error);
  }, []);

  // Handle search
  useEffect(() => {
    if (!query.trim()) {
      setResults([]);
      setIsOpen(false);
      return;
    }

    setIsLoading(true);
    
    // Debounce search
    const timeoutId = setTimeout(() => {
      try {
        const searchResults = search(query);
        const limited = searchResults.slice(0, 10);
        setResults(limited);
        setIsOpen(true);
        trackSearch(query, searchResults.length);
      } catch (error) {
        console.error('Search failed:', error);
        setResults([]);
        trackSearch(query, 0);
      } finally {
        setIsLoading(false);
      }
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [query]);

  // Close results when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (searchRef.current && !searchRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Reset selected index when results change
  useEffect(() => {
    setSelectedIndex(-1);
  }, [results]);

  // Handle keyboard navigation
  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!isOpen || results.length === 0) {
      if (e.key === 'Escape') {
        setIsOpen(false);
        setQuery('');
      }
      return;
    }

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setSelectedIndex((prev) =>
          prev < results.length - 1 ? prev + 1 : prev
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setSelectedIndex((prev) => (prev > 0 ? prev - 1 : -1));
        break;
      case 'Enter':
        e.preventDefault();
        if (selectedIndex >= 0 && selectedIndex < results.length) {
          const result = results[selectedIndex];
          window.location.href = `/${result.category}/${result.slug}`;
          setIsOpen(false);
          setQuery('');
        }
        break;
      case 'Escape':
        e.preventDefault();
        setIsOpen(false);
        inputRef.current?.blur();
        break;
    }
  };

  return (
    <div ref={searchRef} className="relative">
      <div className="relative">
        <svg
          className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-light-text-secondary dark:text-foreground-tertiary pointer-events-none"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
          aria-hidden="true"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
          />
        </svg>
        <input
          ref={inputRef}
          type="search"
          role="combobox"
          aria-label="Search site content"
          aria-autocomplete="list"
          aria-controls="search-results"
          aria-expanded={isOpen && results.length > 0}
          aria-activedescendant={
            selectedIndex >= 0 ? `search-result-${selectedIndex}` : undefined
          }
          aria-busy={isLoading}
          placeholder="Search..."
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onFocus={() => query && setIsOpen(true)}
          onKeyDown={handleKeyDown}
          className="w-full sm:w-64 pl-10 pr-4 py-2 bg-light-surface dark:bg-background border border-light-border dark:border-border rounded-md text-light-text dark:text-foreground placeholder-light-text-secondary dark:placeholder-foreground-muted focus:outline-none focus:border-accent transition-colors"
        />
      </div>

      {/* Search Results Dropdown */}
      {isOpen && (
        <div
          id="search-results"
          role="listbox"
          aria-label="Search results"
          className="absolute top-full left-0 right-0 mt-2 bg-light-surface dark:bg-background border border-light-border dark:border-border rounded-lg shadow-xl max-h-96 overflow-y-auto z-50"
        >
          {isLoading ? (
            <div className="p-4 text-center text-light-text-secondary dark:text-foreground-tertiary" role="status" aria-live="polite">
              Searching...
            </div>
          ) : results.length > 0 ? (
            <ul>
              {results.map((result, index) => (
                <li
                  key={result.id}
                  id={`search-result-${index}`}
                  role="option"
                  aria-selected={selectedIndex === index}
                  className="border-b border-light-border dark:border-border-muted last:border-b-0"
                >
                  <Link
                    href={`/${result.category}/${result.slug}`}
                    onClick={() => {
                      setIsOpen(false);
                      setQuery('');
                    }}
                    className={`block p-4 hover:bg-light-hover dark:hover:bg-accent-muted transition-colors ${
                      selectedIndex === index ? 'bg-light-hover dark:bg-accent-muted' : ''
                    }`}
                    tabIndex={-1}
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex-1 min-w-0">
                        <h3 className="font-semibold text-light-text dark:text-foreground mb-1 truncate">
                          {result.title}
                        </h3>
                        <p className="text-sm text-light-text-secondary dark:text-foreground-secondary line-clamp-2">
                          {result.excerpt}
                        </p>
                      </div>
                      <span className="text-xs text-accent uppercase tracking-wider whitespace-nowrap" aria-label={`Category: ${result.category}`}>
                        {result.category}
                      </span>
                    </div>
                  </Link>
                </li>
              ))}
            </ul>
          ) : query ? (
            <div className="p-4 text-center text-light-text-secondary dark:text-foreground-tertiary" role="status">
              No results found for &quot;{query}&quot;
            </div>
          ) : null}
        </div>
      )}
    </div>
  );
}
